/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import Moment from 'moment';
import {
  faCircleQuestion,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import ClientService from '../../../../../Services/ClientService';
import KYCService from '../../../../../Services/KYCService';
import ScreeningService from '../../../../../Services/ScreeningService';
import Module from '../Module';
import DataPanel from '../../../../../Components/DataPanel/DataPanel';
import LoadingBar from '../../../../../Components/LoadingBar/LoadingBar';
import KYCView from './KYCView';
import XpektorButton from '../../../../../Components/XpektorButton/XpektorButton';
import ScreeningResultModal from '../../../Modals/ScreeningResultModal';
import TabNav from '../../../../../Components/TabNav/TabNav';
import XpektorSlider from '../../../../../Components/XpektorSlider/XpektorSlider';
import CrimeRecordView from './CrimeRecordView';

export default function Regulatory({ client, onPdfClick, isImpersonating }) {
  const [regulatoryInfo, setRegulatoryInfo] = useState({});
  const [payrollData, setPayrollData] = useState([]);
  const [activeDirectors, setActiveDirectors] = useState([]);
  const [inactiveDirectors, setInactiveDirectors] = useState([]);
  const [ongoingMatters, setOngoingMatters] = useState([]);
  const [fetchingRegulatory, setFetchingRegulatory] = useState(false);
  const [regulatoryTab, setRegulatoryTab] = useState(0);

  const [kycStatus, setKYCStatus] = useState({});
  const [screenings, setScreenings] = useState([]);
  const [screeningsFetched, setScreeningsFetched] = useState(false);
  const [screeningInProgress, setScreeningInProgress] = useState(false);
  const [showScreeningResultModal, setShowScreeningResultModal] =
    useState(false);
  const [selectedResult, setSelectedResult] = useState({});

  async function CreateNewKYCProcess() {
    await KYCService.createKYCState(client.id)
      .then(() => {
        KYCService.getKYCOverview(client.id)
          .then((overviewResponse) => {
            setKYCStatus(overviewResponse.data);
          })
          .catch(() => {});
      })
      .catch();
  }

  async function CancelKYCSubmission() {
    await KYCService.cancelKYCSubmission(kycStatus.stateId)
      .then(() => {
        KYCService.getKYCOverview(client.id)
          .then((overviewResponse) => {
            setKYCStatus(overviewResponse.data);
          })
          .catch(() => {});
      })
      .catch(() => {});
  }

  async function RequestScreening() {
    if (screeningsFetched && !screeningInProgress) {
      setScreeningInProgress(true);
      await ScreeningService.startClientAMLScreening(client.id)
        .then(() => {})
        .catch(() => {
          setScreeningInProgress(false);
        })
        .finally(() => {
          ScreeningService.getClientAMLScreenings(client.id).then(
            (newScreeningsResponse) => {
              setScreenings(newScreeningsResponse.data);
            }
          );
        });
    }
  }

  const [count, setCount] = useState(0);
  const getScreeningUpdates = async () => {
    setCount(count + 1);
    await ScreeningService.getClientAMLScreenings(client.id)
      .then((response) => {
        // Find the unfinished screening in CURRENT screenings
        const unfinishedScreening = screenings.find((screening) => {
          return screening.dateFinished === null;
        });

        // Could be the culprit cancelling the polling. Fine to comment out so long as
        // screening is never set to in progress when it shouldn't be.
        if (!unfinishedScreening) {
          // setScreeningInProgress(false);
          return;
        }

        // Check if the unfinished screening exists AND has been completed in the RESPONSE screenings
        const finishedScreening = response.data.find(
          (screening) =>
            screening.id === unfinishedScreening.id &&
            screening.dateFinished !== null
        );

        if (finishedScreening) {
          setScreenings(response.data);
          setScreeningInProgress(false);
        }
      })
      .catch(() => {
        setScreeningInProgress(false);
      });
  };

  useEffect(() => {
    const timer = setTimeout(
      () => screeningInProgress && getScreeningUpdates(),
      5000
    );
    return () => clearTimeout(timer);
  }, [count, screeningInProgress]);

  useEffect(() => {
    const getData = async (clientId) => {
      setFetchingRegulatory(true);

      await ClientService.getRegulatoryInfo(clientId)
        .then((response) => {
          setRegulatoryInfo(response.data);
          setActiveDirectors(response.data.activeDirectors);
          setInactiveDirectors(response.data.inactiveDirectors);
          setPayrollData(response.data.payrollData);
          setOngoingMatters(response.data.ongoingMatters);
        })
        .catch(() => {
          setRegulatoryInfo(null);
        })
        .finally(() => {
          setFetchingRegulatory(false);
        });
    };
    const getKYCData = async (clientId) => {
      await KYCService.getKYCOverview(clientId)
        .then((response) => {
          setKYCStatus(response.data);
        })
        .catch(() => {
          setKYCStatus(null);
        });
    };
    const getAMLScreenings = async (clientId) => {
      await ScreeningService.getClientAMLScreenings(clientId)
        .then((response) => {
          setScreenings(response.data);
          const unfinishedScreening = response.data.find(
            (screening) => screening.dateFinished === null
          );
          if (!unfinishedScreening) {
            setScreeningInProgress(false);
          } else {
            setScreeningInProgress(true);
          }
        })
        .catch(() => {})
        .finally(() => {
          setScreeningsFetched(true);
        });
    };
    if (Object.keys(client).length !== 0 && client.errorCode === null) {
      getData(client.id).then(() => {
        getKYCData(client.id);
        getAMLScreenings(client.id);
      });
    }
  }, [client]);

  const sortedPayrollData = payrollData.sort(
    (a, b) => new Date(b.payrollDate) - new Date(a.payrollDate)
  );

  return (
    <Module>
      {client.errorCode !== null ? (
        <p className="module-empty-text">{client.errorCode}</p>
      ) : fetchingRegulatory ? (
        <LoadingBar active status="Hämtar data..." relative />
      ) : regulatoryInfo === null ? (
        <p className="module-empty-text">Finns ingen data.</p>
      ) : (
        <>
          <Module.Nav>
            <TabNav activeIndex={regulatoryTab} buttonWidth={120}>
              <TabNav.Tab
                title="Regulatoriskt"
                active={regulatoryTab === 0}
                onClick={() => setRegulatoryTab(0)}
                width={120}
              />
              <TabNav.Tab
                title="KYC"
                active={regulatoryTab === 1}
                onClick={() => setRegulatoryTab(1)}
                width={120}
              />
              <TabNav.Tab
                title="Rättslig information"
                active={regulatoryTab === 3}
                onClick={() => setRegulatoryTab(2)}
                width={120}
              />
            </TabNav>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <XpektorButton
                icon={faDownload}
                content="Spara som PDF"
                onClick={() =>
                  onPdfClick(() =>
                    document.getElementById('regulatory-pdf-wrapper')
                  )
                }
              />
            </div>
          </Module.Nav>
          <XpektorSlider activeSlideIndex={regulatoryTab}>
            <XpektorSlider.Slide hidden={regulatoryTab !== 0}>
              <div id="regulatory-pdf-wrapper">
                <Module.DataSection>
                  <Module.DataColumn>
                    <DataPanel title="Företagsinformation">
                      <DataPanel.ValueEntry
                        label="Företagstyp:"
                        value={regulatoryInfo?.companyType ?? 'Uppgift saknas'}
                      />
                      <DataPanel.ValueEntry
                        label="Aktivt:"
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.companyActive === true
                              ? 'Ja'
                              : 'Nej'
                            : 'Uppgift Saknas'
                        }
                      />
                      <DataPanel.ValueEntry
                        label="Antal anställda:"
                        value={regulatoryInfo?.nrEmployees ?? 'Uppgift saknas'}
                      />
                      <DataPanel.ValueEntry
                        label="SNI (Bransch):"
                        value={
                          regulatoryInfo?.sni || regulatoryInfo?.sniDescription
                            ? `${regulatoryInfo.sni ?? ''}${
                                regulatoryInfo?.sni &&
                                regulatoryInfo.sniDescription
                                  ? ','
                                  : ''
                              } ${regulatoryInfo.sniDescription ?? ''}`
                            : 'Uppgift saknas'
                        }
                      />
                      <DataPanel.ValueEntry
                        label="Riskbransch:"
                        value={
                          regulatoryInfo?.riskBranch !== null
                            ? regulatoryInfo.riskBranch === true
                              ? 'Ja'
                              : 'Nej'
                            : 'Uppgift Saknas'
                        }
                        error={regulatoryInfo?.riskBranch === true}
                      />
                      <DataPanel.ValueEntry
                        label="Senaste adressändring:"
                        value={
                          regulatoryInfo?.dateAddressChanged
                            ? Moment(regulatoryInfo.dateAddressChanged).format(
                                'YYYY-MM-DD'
                              )
                            : 'Uppgift saknas'
                        }
                      />
                      <DataPanel.ValueEntry
                        label="Tidigare namn:"
                        value={regulatoryInfo?.oldName ?? 'Uppgift saknas'}
                      />
                      {regulatoryInfo?.companyType === 'EF' ? (
                        <DataPanel.ValueEntry
                          label="Namn på innehavare (enskild firma):"
                          value={
                            regulatoryInfo?.nonLtdOwnerName &&
                            regulatoryInfo.nonLtdOwnerName.length > 0
                              ? regulatoryInfo.nonLtdOwnerName
                              : 'Uppgift saknas'
                          }
                        />
                      ) : (
                        ''
                      )}
                      <DataPanel.ValueEntry
                        label="Moderbolag:"
                        value={
                          regulatoryInfo?.groupMotherName &&
                          regulatoryInfo.groupMotherName.length > 0
                            ? regulatoryInfo.groupMotherName
                            : 'Uppgift saknas'
                        }
                      />
                    </DataPanel>
                  </Module.DataColumn>
                  <Module.DataColumn>
                    <DataPanel title="Styrelsemedlemmar" scroll maxHeight={488}>
                      <DataPanel.Subtitle title="AKTIVA" green />
                      {activeDirectors.length === 0 ? (
                        <DataPanel.ValueEntry label="Finns inga" />
                      ) : (
                        activeDirectors.map((director) => {
                          return (
                            <DataPanel.DoubleValueEntry
                              key={director.id}
                              title={director.name}
                              subTitle={director.function}
                              valueOne={Moment(director.appointmentDate).format(
                                'YYYY-MM-DD'
                              )}
                              valueTwo={
                                director.exitingDate
                                  ? Moment(director.exitingDate).format(
                                      'YYYY-MM-DD'
                                    )
                                  : null
                              }
                              valueOneName="Tillträdde:"
                              valueTwoName="Avgick:"
                            />
                          );
                        })
                      )}
                      <DataPanel.Subtitle title="INAKTIVA" red />
                      {inactiveDirectors.length !== 0 ? (
                        inactiveDirectors.map((director) => {
                          return (
                            <DataPanel.DoubleValueEntry
                              key={director.id}
                              title={director.name}
                              subTitle={director.function}
                              valueOne={Moment(director.appointmentDate).format(
                                'YYYY-MM-DD'
                              )}
                              valueTwo={
                                director.exitingDate
                                  ? Moment(director.exitingDate).format(
                                      'YYYY-MM-DD'
                                    )
                                  : null
                              }
                              valueOneName="Tillträdde:"
                              valueTwoName="Avgick:"
                            />
                          );
                        })
                      ) : (
                        <DataPanel.ValueEntry label="Finns inga" />
                      )}
                    </DataPanel>
                  </Module.DataColumn>
                  <Module.DataColumn>
                    <DataPanel title="Regulatoriska krav">
                      <DataPanel.ValueEntry
                        label="F-skatt:"
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.fTax === true
                              ? 'Ja'
                              : regulatoryInfo.fTaxDeregistrationReason !== null
                              ? `Avregistrerad: ${regulatoryInfo.fTaxDeregistrationReason}`
                              : 'Nej'
                            : 'Uppgift saknas'
                        }
                        valueType=""
                      />
                      <DataPanel.ValueEntry
                        label="Momsregistrering:"
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.moms === true
                              ? 'Ja'
                              : 'Nej'
                            : 'Uppgift saknas'
                        }
                        valueType=""
                      />
                      <DataPanel.ValueEntry
                        label="Registrerad i arbetsgivarregistret:"
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.empTax === true
                              ? 'Ja'
                              : 'Nej'
                            : 'Uppgift saknas'
                        }
                      />
                      <DataPanel.ValueEntry
                        label="Revisorsförbehåll:"
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.auditorReservation === true
                              ? 'Ja'
                              : 'Nej'
                            : 'Uppgift saknas'
                        }
                      />
                    </DataPanel>
                    <DataPanel title="Skatteverket">
                      <DataPanel.ValueEntry
                        label="Saldo skattekonto:"
                        value={
                          client.skvConnected && client.taxBalance != null
                            ? client.taxBalance
                            : 'Ej kopplat'
                        }
                        valueType={
                          client.skvConnected && client.taxBalance != null
                            ? 'currency'
                            : ''
                        }
                      />
                    </DataPanel>
                  </Module.DataColumn>
                </Module.DataSection>
                <Module.DataSection>
                  <Module.DataColumn>
                    <DataPanel
                      title="Inbetalda sociala avgifter"
                      scroll
                      maxHeight={410}
                    >
                      {sortedPayrollData.map((data) => {
                        return (
                          <DataPanel.DoubleValueEntry
                            key={data.id}
                            title={`${data.fee} kr`}
                            valueOne={Moment(data.payrollDate).format(
                              'YYYY-MM-DD'
                            )}
                            valueTwo={Moment(data.registerDate).format(
                              'YYYY-MM-DD'
                            )}
                            valueOneName="Datum:"
                            valueTwoName="Registrerat:"
                          />
                        );
                      })}
                    </DataPanel>
                  </Module.DataColumn>
                  <DataPanel
                    title="PEP/AML Screening"
                    icon={faCircleQuestion}
                    iconHoverContent={
                      <p>
                        Screening av AML, PEP och adverse media innebär att ett
                        företag genomför kontroller för att identifiera
                        potentiella risker relaterade till penningtvätt (AML),
                        politiskt utsatta personer (PEP) och negativa nyheter
                        eller mediaomdömen om individer eller företag (adverse
                        media). <br />
                        <br />
                        För företaget genomför vi screening på PEP, sanktioner,
                        adverse media, profile of interest, enforcement och
                        state owned enterprise. För personerna i styrelsen
                        screenar vi på PEP, sanktioner, adverse media, profile
                        of interest och enforcement. Dessa screeningar är
                        viktiga för att säkerställa att företaget följer regler
                        och bestämmelser för att förebygga olagliga aktiviteter
                        och minska risken för att bli involverad i ekonomiska
                        brott.
                        <br />
                        <br />
                        Kostnad tillkommer per screening på nästa faktura.
                      </p>
                    }
                    minHeight={128}
                    maxHeight={410}
                    scroll
                    footer={
                      <>
                        <XpektorButton
                          content="Screena"
                          onClick={() => RequestScreening()}
                          loading={!screeningsFetched}
                          disabled={
                            screeningInProgress ||
                            !screeningsFetched ||
                            isImpersonating
                          }
                        />
                        <p>
                          <i>
                            Kostnad tillkommer per screening på nästa faktura.
                          </i>
                        </p>
                      </>
                    }
                  >
                    {screenings.length > 0 ? (
                      screenings
                        .sort(
                          (a, b) =>
                            new Date(b.dateStarted) - new Date(a.dateStarted)
                        )
                        .map((screening) => {
                          return (
                            <DataPanel.ScreeningEntry
                              key={screening.id}
                              entry={screening}
                              onResultClick={(result) => {
                                setSelectedResult(result);
                                setShowScreeningResultModal(true);
                              }}
                            />
                          );
                        })
                    ) : (
                      <DataPanel.ValueEntry label="Finns inga screeningar" />
                    )}
                  </DataPanel>
                  <Module.DataColumn>
                    <DataPanel title="Pågående ärenden" scroll maxHeight={410}>
                      {ongoingMatters.length === 0 ? (
                        <DataPanel.ValueEntry label="Ingen Data" />
                      ) : (
                        ongoingMatters.map((data) => {
                          return (
                            <DataPanel.DoubleValueEntry
                              key={data.id}
                              title={data.matterText}
                              subTitle={data.caseNumber}
                              valueOne={
                                data.dateAdded &&
                                Moment(data.dateAdded).format('YYYY-MM-DD')
                              }
                              valueOneName="Datum:"
                            />
                          );
                        })
                      )}
                    </DataPanel>
                  </Module.DataColumn>
                </Module.DataSection>
              </div>
            </XpektorSlider.Slide>
            <XpektorSlider.Slide hidden={regulatoryTab !== 1}>
              <KYCView
                kycStatus={kycStatus}
                onCreateClick={() => CreateNewKYCProcess()}
                onCancelClick={() => CancelKYCSubmission()}
                onResultClick={(result) => {
                  setSelectedResult(result);
                  setShowScreeningResultModal(true);
                }}
                isImpersonating={isImpersonating}
              />
            </XpektorSlider.Slide>
            <XpektorSlider.Slide hidden={regulatoryTab !== 2}>
              <CrimeRecordView
                clientId={client.id}
                isImpersonating={isImpersonating}
              />
            </XpektorSlider.Slide>
          </XpektorSlider>
        </>
      )}
      {showScreeningResultModal ? (
        <ScreeningResultModal
          handleClose={() => {
            setShowScreeningResultModal(false);
            setSelectedResult({});
          }}
          result={selectedResult}
        />
      ) : (
        ''
      )}
    </Module>
  );
}
